/**
 * Página pública da captura por QR — o que o CLIENTE vê no celular (mockup 06,
 * 390px). Fora do ProtectedRoute, sem socket, marca da EMPRESA do vendedor
 * (D6 — `company.primaryColor` vira `--capture-brand`). SEMPRE tema claro
 * (decisão do dono, 2026-09-04) e fonte fixada aqui — a rota vive fora do
 * ThemeProvider, então nada do tema (nem a fonte) chega até ela. 404/410 da
 * API viram "Este link expirou".
 */
import type { LeadCaptureFieldMode, LeadCapturePublicInfo } from '@troia-v3/types';
import { formatCpf, isValidBirthDate, isValidCpf, resolveLeadCaptureFields } from '@troia-v3/types';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useApp } from '@/contexts/AppContext';
import { Icon } from '@/shared/icons';

import { captureApi } from '../services/captureApi';

import { isPhoneComplete, maskBrPhone, phoneToE164Input } from './phoneMask';

type PublicState = 'loading' | 'form' | 'whatsapp' | 'done' | 'expired' | 'error';

const apiStatus = (error: unknown): number | undefined => (error as { response?: { status?: number } }).response?.status;
const apiMessage = (error: unknown): string | undefined => (error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }).response?.data?.message;
const apiFieldErrors = (error: unknown): Record<string, string[]> => (error as { response?: { data?: { errors?: Record<string, string[]> } } }).response?.data?.errors ?? {};

/**
 * `app.websiteUrl` é gravado sem esquema (ex.: `troiachat.com`) — sem o
 * `https://` o `<a>` viraria link relativo dentro da própria página.
 */
export const toAbsoluteUrl = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim() ?? '';
  if (trimmed === '') return undefined;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/** Mesma pilha do tema padrão do app (Inter vem do `index.html`), fixada porque a página fica fora do ThemeProvider. */
const FONT_FAMILY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/** Paleta clara fixa — a página pública não segue tema do sistema nem do tenant. */
const palette = { bg: '#f4f5f8', surface: '#ffffff', ink: '#1e293b', sec: '#334155', muted: '#6b7280', faint: '#94a3b8', border: 'rgba(0,0,0,.09)', borderStrong: 'rgba(0,0,0,.13)' } as const;

/** `#rgb` / `#rrggbb` / `#rrggbbaa` — protege `--capture-brand` contra valor vazio/malformado vindo de `company.primaryColor`. */
const isValidHexColor = (value: string): boolean => /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value);

/**
 * Número do canal (E.164 no path do `wa.me`) formatado como BR pro fallback
 * "copiar o código" — em vez de dígitos crus. Assume DDI 55 (canal comercial
 * brasileiro); qualquer outro DDI cai no formato `+{dígitos}` sem máscara.
 */
/**
 * Quebra o texto da mensagem nos valores que vão em negrito (código e cupom),
 * na posição em que aparecem — a mensagem pode ter os dois em qualquer ordem.
 */
export const splitMessageTokens = (text: string, tokens: string[]): Array<{ text: string; token: boolean }> => {
  const values = tokens.filter((t) => t !== '');
  if (values.length === 0) return [{ text, token: false }];
  const escaped = values.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return text
    .split(new RegExp(`(${escaped.join('|')})`))
    .filter((part) => part !== '')
    .map((part) => ({ text: part, token: values.includes(part) }));
};

/** Máscara progressiva `DD/MM/AAAA` — o teclado numérico do celular é mais rápido que o calendário para um ano distante. */
export const maskBirthDate = (value: string): string =>
  value.replace(/\D/g, '').slice(0, 8).replace(/(\d{2})(\d)/, '$1/$2').replace(/(\d{2})(\d)/, '$1/$2');

/** `DD/MM/AAAA` → `AAAA-MM-DD` (formato da API); `null` se incompleta. */
export const birthDateToIso = (masked: string): string | null => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(masked);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : null;
};

const formatChannelPhone = (whatsappUrl: string): string => {
  const digits = new URL(whatsappUrl).pathname.replace(/\D/g, '');
  if (digits.startsWith('55') && digits.length > 2) return `+55 ${maskBrPhone(digits.slice(2))}`;
  return `+${digits}`;
};

export const LeadCapturePublicPage: React.FC = () => {
  const { uuid = '' } = useParams<{ uuid: string }>();
  const { app } = useApp();

  const [state, setState] = useState<PublicState>('loading');
  const [info, setInfo] = useState<LeadCapturePublicInfo | null>(null);
  const [name, setName] = useState('');
  const [ddi, setDdi] = useState('55');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [cpf, setCpf] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{ whatsappUrl?: string; code: string; coupon?: string } | null>(null);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    if (!uuid) { setState('expired'); return; }
    let alive = true;
    captureApi.getPublic(uuid)
      .then((data) => {
        if (!alive) return;
        setInfo(data);
        // Defensivo: o backend já devolve 410 para sessão fora de `waiting_scan`,
        // mas se o link for reaberto depois de preenchido/confirmado/expirado a
        // página trata como "Este link expirou" (mesma copy da tela 4).
        setState(data.status === 'waiting_scan' ? 'form' : 'expired');
      })
      .catch((error: unknown) => {
        if (!alive) return;
        const status = apiStatus(error);
        // D2 (fix round final): uuid malformado no path cai em 422 (Zod da
        // rota pública) — mesma copy "Este link expirou" dos casos 404/410,
        // nunca a tela genérica de erro (o link em si é que é inválido).
        setState(status === 404 || status === 410 || status === 422 ? 'expired' : 'error');
      });
    return () => { alive = false; };
  }, [uuid]);

  useEffect(() => { setLogoError(false); }, [info?.company.logo]);

  const rawBrand = info?.company.primaryColor?.trim() ?? '';
  const brand = rawBrand && isValidHexColor(rawBrand) ? rawBrand : '#1d4ed8';
  const companyName = info?.company.name ?? '';
  const sellerName = info?.seller.firstName ?? '';
  // Servidor anterior a 2026-10-21 não manda `fields` — cai no `emailRequired`.
  const fields = info?.fields ?? resolveLeadCaptureFields({ emailRequired: info?.emailRequired === true });

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!info) return;
    const showEmail = fields.email !== 'hidden';
    const showCpf = fields.cpf !== 'hidden';
    const birthDateIso = birthDateToIso(birthDate);
    const next: Record<string, string> = {};
    if (name.trim().length < 2) next.name = 'Informe seu nome';
    if (!isPhoneComplete(ddi, phone)) next.phone = 'Informe um WhatsApp válido';
    if (showEmail) {
      if (fields.email === 'required' && email.trim() === '') next.email = 'Informe seu e-mail';
      if (email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = 'E-mail inválido';
    }
    if (showCpf) {
      if (fields.cpf === 'required' && cpf === '') next.cpf = 'Informe seu CPF';
      if (cpf !== '' && !isValidCpf(cpf)) next.cpf = 'CPF inválido';
    }
    if (fields.birthDate !== 'hidden') {
      if (fields.birthDate === 'required' && birthDate === '') next.birthDate = 'Informe sua data de nascimento';
      if (birthDate !== '' && (birthDateIso === null || !isValidBirthDate(birthDateIso))) next.birthDate = 'Data de nascimento inválida';
    }
    if (!consent) next.consent = 'É preciso aceitar para continuar';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      const res = await captureApi.submitPublic(uuid, {
        name: name.trim(),
        phone: phoneToE164Input(ddi, phone),
        ...(showEmail && email.trim() ? { email: email.trim() } : {}),
        ...(showCpf && cpf ? { cpf } : {}),
        ...(fields.birthDate !== 'hidden' && birthDateIso ? { birthDate: birthDateIso } : {}),
        consent: true,
      });
      setResult({
        code: res.code,
        ...(res.whatsappUrl ? { whatsappUrl: res.whatsappUrl } : {}),
        ...(res.coupon ? { coupon: res.coupon } : {}),
      });
      setState(res.whatsappUrl ? 'whatsapp' : 'done');
    } catch (error: unknown) {
      const status = apiStatus(error);
      if (status === 404 || status === 410) { setState('expired'); return; }
      if (status === 422) {
        const fields = apiFieldErrors(error);
        const mapped = Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v[0] ?? 'Campo inválido']));
        setErrors(Object.keys(mapped).length > 0 ? mapped : { _form: apiMessage(error) ?? 'Confira os dados e tente de novo' });
        return;
      }
      if (status === 429) { setErrors({ _form: 'Muitas tentativas. Aguarde um minuto e tente de novo.' }); return; }
      setErrors({ _form: 'Não foi possível enviar agora. Tente novamente em instantes.' });
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = async (): Promise<void> => {
    if (!result) return;
    try { await navigator.clipboard.writeText(result.code); } catch { /* sem clipboard (http) — o código está visível na tela */ }
  };

  const inputStyle: React.CSSProperties = { minHeight: 46, border: `1px solid ${palette.borderStrong}`, borderRadius: 10, background: palette.surface, color: palette.ink, fontSize: 15, padding: '12px 14px', width: '100%' };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, color: palette.sec, marginBottom: 7 };
  const requiredTag = (mode: LeadCaptureFieldMode): React.ReactNode => (mode === 'required'
    ? <span style={{ color: '#ef4444' }}>*</span>
    : <span style={{ color: palette.faint, fontWeight: 500 }}>(opcional)</span>);

  const Brandbar = (
    <div className="flex items-center gap-[11px] mt-1.5 mb-4" data-testid="capture-public-brand">
      <span className="flex items-center justify-center overflow-hidden flex-none" style={{ width: 42, height: 42, borderRadius: 11, background: 'var(--capture-brand)', color: '#fff', fontWeight: 900, fontSize: 20 }}>
        {info?.company.logo && !logoError
          ? <img src={info.company.logo} alt="" onError={() => setLogoError(true)} className="w-full h-full object-cover" />
          : (companyName[0] ?? '').toUpperCase()}
      </span>
      <div className="min-w-0">
        <div className="font-bold text-base tracking-[-0.02em] truncate" style={{ color: palette.ink }}>{companyName}</div>
        {info && <div className="text-xs" style={{ color: palette.muted }}>Atendimento na loja</div>}
      </div>
    </div>
  );
  const poweredName = app?.name ?? 'Troia Chat';
  const poweredUrl = toAbsoluteUrl(app?.websiteUrl);
  const Powered = (
    <div className="text-center text-xs mt-[18px]" style={{ color: palette.faint }} data-testid="capture-public-powered">
      Seguro · feito com{' '}
      {poweredUrl
        ? <a href={poweredUrl} target="_blank" rel="noopener noreferrer" className="font-semibold underline-offset-2 hover:underline" style={{ color: palette.muted }} data-testid="capture-public-powered-link">{poweredName}</a>
        : <b style={{ color: palette.muted }}>{poweredName}</b>}
    </div>
  );
  const Steps = ({ on }: { on: number }) => (
    <div className="flex justify-center gap-1.5 mb-3.5" aria-hidden>
      {[0, 1, 2].map((i) => <i key={i} className="block rounded-full" style={{ width: 28, height: 4, background: i <= on ? 'var(--capture-brand)' : palette.border }} />)}
    </div>
  );
  // Cupom de sorteio (2026-09-29): só quando o funil dá cupom e este cadastro ganhou um.
  const CouponCard = result?.coupon ? (
    <div className="mb-4 rounded-[12px]" style={{ padding: '10px 12px', background: '#fef3c7', color: '#92400e' }} data-testid="capture-public-coupon">
      <div className="text-xs font-semibold">Seu cupom do sorteio</div>
      <div className="text-[26px] font-bold tracking-[0.04em]" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="capture-public-coupon-number">{result.coupon}</div>
    </div>
  ) : null;
  const Seller = ({ full }: { full?: boolean }) => (
    <div className="flex items-center gap-2.5 mt-3.5 text-[13px]" style={{ padding: '10px 12px', border: `1px solid ${palette.border}`, borderRadius: 12, background: palette.surface, textAlign: 'left' }} data-testid="capture-public-seller">
      <span className="flex items-center justify-center flex-none text-white text-xs font-semibold" style={{ width: 32, height: 32, borderRadius: '32%', background: '#64748b' }}>{sellerName[0] ?? ''}</span>
      <div className="min-w-0">
        <div style={{ color: palette.ink }}><b>{full ? sellerName : `Atendido por ${sellerName}`}</b></div>
        <div className="text-xs" style={{ color: palette.muted }}>{companyName}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: palette.bg, color: palette.ink, fontFamily: FONT_FAMILY, fontSize: 14, lineHeight: 1.5, ['--capture-brand' as string]: brand } as React.CSSProperties} data-testid="capture-public">
      <div className="max-w-[520px] mx-auto" style={{ padding: '14px 18px 28px' }} data-testid="capture-public-state" data-state={state}>
        {Brandbar}

        {state === 'loading' && <div className="text-center py-16 text-sm" style={{ color: palette.muted }}>Carregando…</div>}

        {state === 'form' && info && (
          <>
            <Steps on={0} />
            <div style={{ background: palette.surface, border: `1px solid ${palette.border}`, borderBottom: 'none', borderRadius: '16px 16px 0 0', padding: '20px 20px 16px' }}>
              <h1 className="m-0 mb-1.5 text-[21px] font-bold tracking-[-0.02em]">Cadastre-se e concorra a prêmios</h1>
              <p className="m-0" style={{ color: palette.muted, lineHeight: 1.55 }}>Leva menos de um minuto. Seu atendimento continua aqui na loja.</p>
            </div>
            <form onSubmit={(e) => { void handleSubmit(e); }} noValidate style={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: '0 0 16px 16px', padding: '18px 20px 20px' }}>
              <div className="mb-[15px]">
                <label style={labelStyle} htmlFor="capture-name">Seu nome <span style={{ color: '#ef4444' }}>*</span></label>
                <input id="capture-name" style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" data-testid="capture-public-name" />
                {errors.name && <div className="text-xs mt-1" style={{ color: '#ef4444' }} data-testid="capture-public-error-name">{errors.name}</div>}
              </div>
              <div className="mb-[15px]">
                <label style={labelStyle} htmlFor="capture-phone">Seu WhatsApp <span style={{ color: '#ef4444' }}>*</span></label>
                <div className="flex items-center gap-2" style={{ ...inputStyle, padding: '0 14px' }}>
                  <span className="flex items-center gap-0.5 font-semibold text-sm" style={{ color: palette.muted, borderRight: `1px solid ${palette.borderStrong}`, paddingRight: 8 }}>
                    +<input aria-label="DDI" value={ddi} onChange={(e) => setDdi(e.target.value.replace(/\D/g, '').slice(0, 3))} inputMode="numeric" className="bg-transparent outline-none" style={{ width: 28, color: palette.muted }} data-testid="capture-public-ddi" />
                  </span>
                  <input
                    id="capture-phone"
                    className="flex-1 bg-transparent outline-none"
                    style={{ minHeight: 44, color: palette.ink, fontVariantNumeric: 'tabular-nums' }}
                    value={ddi === '55' ? maskBrPhone(phone) : phone.replace(/\D/g, '')}
                    onChange={(e) => setPhone(e.target.value)}
                    inputMode="tel"
                    autoComplete="tel-national"
                    placeholder={ddi === '55' ? '(47) 99999-0000' : 'número'}
                    data-testid="capture-public-phone"
                  />
                </div>
                {errors.phone && <div className="text-xs mt-1" style={{ color: '#ef4444' }} data-testid="capture-public-error-phone">{errors.phone}</div>}
              </div>
              {fields.email !== 'hidden' && (
                <div className="mb-[15px]">
                  <label style={labelStyle} htmlFor="capture-email">Seu e-mail {requiredTag(fields.email)}</label>
                  <input id="capture-email" style={inputStyle} value={email} onChange={(e) => setEmail(e.target.value)} type="email" inputMode="email" autoComplete="email" placeholder="voce@email.com" data-testid="capture-public-email" />
                  {errors.email && <div className="text-xs mt-1" style={{ color: '#ef4444' }} data-testid="capture-public-error-email">{errors.email}</div>}
                </div>
              )}
              {fields.cpf !== 'hidden' && (
                <div className="mb-[15px]">
                  <label style={labelStyle} htmlFor="capture-cpf">Seu CPF {requiredTag(fields.cpf)}</label>
                  <input id="capture-cpf" style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }} value={cpf} onChange={(e) => setCpf(formatCpf(e.target.value))} inputMode="numeric" autoComplete="off" placeholder="000.000.000-00" maxLength={14} data-testid="capture-public-cpf" />
                  {errors.cpf && <div className="text-xs mt-1" style={{ color: '#ef4444' }} data-testid="capture-public-error-cpf">{errors.cpf}</div>}
                </div>
              )}
              {fields.birthDate !== 'hidden' && (
                <div className="mb-[15px]">
                  <label style={labelStyle} htmlFor="capture-birth-date">Data de nascimento {requiredTag(fields.birthDate)}</label>
                  <input id="capture-birth-date" style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }} value={birthDate} onChange={(e) => setBirthDate(maskBirthDate(e.target.value))} inputMode="numeric" autoComplete="off" placeholder="DD/MM/AAAA" maxLength={10} data-testid="capture-public-birth-date" />
                  {errors.birthDate && <div className="text-xs mt-1" style={{ color: '#ef4444' }} data-testid="capture-public-error-birth-date">{errors.birthDate}</div>}
                </div>
              )}
              <label className="flex gap-2.5 text-[12.5px] mb-[18px] cursor-pointer" style={{ color: palette.sec, lineHeight: 1.45 }}>
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="sr-only" data-testid="capture-public-consent" />
                <span aria-hidden className="flex items-center justify-center flex-none mt-px" style={{ width: 20, height: 20, borderRadius: 6, border: `1.6px solid ${consent ? 'var(--capture-brand)' : palette.borderStrong}`, background: consent ? 'var(--capture-brand)' : 'transparent', color: '#fff' }}>
                  {consent && <Icon name="check" size={14} />}
                </span>
                <span>{info.consentText}</span>
              </label>
              {errors.consent && <div className="text-xs -mt-3 mb-3" style={{ color: '#ef4444' }} data-testid="capture-public-error-consent">{errors.consent}</div>}
              {errors._form && <div className="text-[13px] mb-3 rounded-[10px]" style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 12px' }} data-testid="capture-public-error">{errors._form}</div>}
              <button type="submit" disabled={submitting} className="w-full flex items-center justify-center gap-2 font-bold text-base text-white border-0 disabled:opacity-70" style={{ height: 50, borderRadius: 10, background: 'var(--capture-brand)' }} data-testid="capture-public-submit">
                {submitting ? 'Enviando…' : 'Enviar'}<Icon name="arrow-right" size={18} />
              </button>
            </form>
            <Seller />
            {Powered}
          </>
        )}

        {state === 'whatsapp' && result?.whatsappUrl && (
          <>
            <Steps on={1} />
            <div className="text-center" style={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 16, padding: '18px 20px 20px' }}>
              <div className="mx-auto mb-3.5 flex items-center justify-center rounded-full" style={{ width: 64, height: 64, background: `${brand}22`, color: 'var(--capture-brand)' }}><Icon name="check" size={30} /></div>
              <h2 className="m-0 mb-1.5 text-[21px] font-bold tracking-[-0.02em]">Falta só confirmar</h2>
              <p className="m-0 mb-4" style={{ color: palette.muted, lineHeight: 1.55 }}>Toque no botão abaixo — o WhatsApp abre com a mensagem pronta. É só enviar.</p>
              {CouponCard}
              <div className="text-left mb-4" style={{ background: '#dcf8c6', color: '#1f2937', borderRadius: '12px 12px 12px 2px', padding: '10px 12px', fontSize: 14, lineHeight: 1.45 }} data-testid="capture-public-message">
                {splitMessageTokens(new URL(result.whatsappUrl).searchParams.get('text') ?? '', [result.code, result.coupon ?? '']).map((part, index) =>
                  part.token
                    ? <b key={index} style={{ fontVariantNumeric: 'tabular-nums' }} {...(part.text === result.code ? { 'data-testid': 'capture-public-code' } : {})}>{part.text}</b>
                    : <React.Fragment key={index}>{part.text}</React.Fragment>,
                )}
              </div>
              <a href={result.whatsappUrl} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 font-bold text-base text-white" style={{ height: 50, borderRadius: 10, background: '#25d366' }} data-testid="capture-public-whatsapp">
                <Icon name="message-circle" size={22} />Confirmar no WhatsApp
              </a>
              <div className="text-xs mt-3" style={{ color: palette.muted }}>
                O WhatsApp não abriu? <button type="button" className="underline min-h-[44px]" onClick={() => { void copyCode(); }} data-testid="capture-public-copy-code">Copiar o código</button> e enviar para <b style={{ fontVariantNumeric: 'tabular-nums' }}>{formatChannelPhone(result.whatsappUrl)}</b>.
              </div>
            </div>
            {Powered}
          </>
        )}

        {state === 'done' && (
          <>
            <Steps on={2} />
            <div className="text-center" style={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 16, padding: '18px 20px 20px' }}>
              <div className="mx-auto mb-3.5 flex items-center justify-center rounded-full" style={{ width: 64, height: 64, background: '#dcfce7', color: '#16a34a' }}><Icon name="check" size={30} /></div>
              <h2 className="m-0 mb-1.5 text-[21px] font-bold tracking-[-0.02em]">Tudo certo, {name.trim().split(' ')[0]}!</h2>
              <p className="m-0 mb-4" style={{ color: palette.muted, lineHeight: 1.55 }}>Seu cadastro foi confirmado. {sellerName} continua seu atendimento aqui na loja — e a gente fala com você pelo WhatsApp.</p>
              {CouponCard}
              <Seller full />
            </div>
            {Powered}
          </>
        )}

        {state === 'expired' && (
          <>
            <div className="text-center mt-[26px]" style={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 16, padding: '18px 20px 20px' }}>
              <div className="mx-auto mb-3.5 flex items-center justify-center rounded-full" style={{ width: 64, height: 64, background: '#f1f5f9', color: '#94a3b8' }}><Icon name="clock" size={30} /></div>
              <h2 className="m-0 mb-1.5 text-[21px] font-bold tracking-[-0.02em]">Este link expirou</h2>
              <p className="m-0 mb-4" style={{ color: palette.muted, lineHeight: 1.55 }}>O QR Code vale para um único atendimento. Peça ao vendedor para gerar um novo código.</p>
              <button type="button" onClick={() => window.close()} className="w-full font-semibold" style={{ height: 46, borderRadius: 10, background: 'transparent', color: palette.sec, border: `1px solid ${palette.borderStrong}` }} data-testid="capture-public-close">Fechar</button>
            </div>
            {Powered}
          </>
        )}

        {state === 'error' && (
          <div className="text-center mt-[26px]" style={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 16, padding: '18px 20px 20px' }} data-testid="capture-public-error">
            <h2 className="m-0 mb-1.5 text-[21px] font-bold tracking-[-0.02em]">Não foi possível abrir</h2>
            <p className="m-0 mb-4" style={{ color: palette.muted }}>Confira sua conexão e tente abrir o QR Code de novo.</p>
            <button type="button" onClick={() => window.location.reload()} className="w-full font-bold text-white" style={{ height: 50, borderRadius: 10, background: 'var(--capture-brand)' }}>Tentar de novo</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadCapturePublicPage;
